import React, { useState, useEffect, useRef } from 'react';
import './AlertsTable.css';

const AlertsTable = ({ alerts, openAlertDetails, selectedAlerts, setSelectedAlerts }) => {
  const [activeTab, setActiveTab] = useState('Alerts Aggregated');
  const [expandedRows, setExpandedRows] = useState({});
  const [loading, setLoading] = useState(false);
  const [displayedAlerts, setDisplayedAlerts] = useState([]);
  const [tabLineCounts, setTabLineCounts] = useState({
    'Simple': 0,
    'Alerts Aggregated': 0,
    'Hosts Aggregated': 0
  });

  // Reference to the displayed alerts to prevent duplicate data
  const processedAlertsRef = useRef(new Set());

  // Reset the processed alerts reference when the alerts prop changes
  useEffect(() => {
    processedAlertsRef.current = new Set();

    // Filter out any duplicates based on unique IDs
    const uniqueAlerts = alerts.filter(alert => {
      const isDuplicate = processedAlertsRef.current.has({ id: alert._id, index: alert._index });
      if (!isDuplicate) {
        processedAlertsRef.current.add({ id: alert._id, index: alert._index });
        return true;
      }
      return false;
    });

    setDisplayedAlerts(uniqueAlerts);

    // Calculate line counts for each tab
    const simpleTableLines = uniqueAlerts.length;

    const alertsAggregatedLines = getAggregatedData('description', uniqueAlerts).length;

    const hostsAggregatedLines = getAggregatedData('organization_hostname', uniqueAlerts).length;

    setTabLineCounts({
      'Simple': simpleTableLines,
      'Alerts Aggregated': alertsAggregatedLines,
      'Hosts Aggregated': hostsAggregatedLines
    });
  }, [alerts]);

  const handleSelectAll = (checked, visibleAlerts) => {
    if (checked) {
      const allVisibleIds = visibleAlerts.map((alert) => { return { id: alert._id, index: alert._index }; });
      setSelectedAlerts(allVisibleIds);
    } else {
      setSelectedAlerts([]);
    }
  };

  // New function to handle group-level selection
  const handleSelectGroup = (checked, groupAlerts) => {
    const groupIds = groupAlerts.map(alert => ({ id: alert._id, index: alert._index }));

    if (checked) {
      // Add all alerts from this group to selection
      setSelectedAlerts(prev => {
        const newSelection = [...prev];
        groupIds.forEach(alertId => {
          if (!newSelection.some(a => a.id === alertId.id && a.index === alertId.index)) {
            newSelection.push(alertId);
          }
        });
        return newSelection;
      });
    } else {
      // Remove all alerts from this group from selection
      setSelectedAlerts(prev =>
        prev.filter(selected =>
          !groupIds.some(groupAlert =>
            groupAlert.id === selected.id && groupAlert.index === selected.index
          )
        )
      );
    }
  };

  const handleSelectRow = (alertId, alertIndex, checked) => {
    setSelectedAlerts((prev) =>
      checked ? [...prev, { id: alertId, index: alertIndex }] : prev.filter((alert) => alert.id !== alertId && alert.index !== alertIndex)
    );
  };

  const toggleRowExpansion = (key) => {
    setExpandedRows((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Helper function to check if all alerts in a group are selected
  const areAllGroupAlertsSelected = (groupAlerts) => {
    if (groupAlerts.length === 0) return false;
    return groupAlerts.every(alert =>
      selectedAlerts.some(selected => selected.id === alert._id && selected.index === alert._index)
    );
  };

  const getAggregatedData = (groupBy, data) => {
    const groupedData = data.reduce((acc, alert) => {
      // Skip if the alert doesn't have a valid ID or required property
      if (!alert._id || !alert[groupBy]) return acc;

      const key = alert[groupBy];
      if (!acc[key]) {
        acc[key] = {
          key,
          count: 1,
          firstTimestamp: new Date(alert.timestamp),
          lastTimestamp: new Date(alert.timestamp),
          alerts: [alert],
          // Track unique descriptions and hosts
          uniqueDescriptions: new Set([alert.description]),
          uniqueHosts: new Set([alert.organization_ip])
        };
      } else {
        acc[key].count += 1;
        acc[key].firstTimestamp = new Date(
          Math.min(acc[key].firstTimestamp.getTime(), new Date(alert.timestamp).getTime())
        );
        acc[key].lastTimestamp = new Date(
          Math.max(acc[key].lastTimestamp.getTime(), new Date(alert.timestamp).getTime())
        );
        acc[key].alerts.push(alert);

        // Add unique descriptions and hosts
        acc[key].uniqueDescriptions.add(alert.description);
        acc[key].uniqueHosts.add(alert.organization_ip);
      }
      return acc;
    }, {});

    return Object.values(groupedData).map((item) => ({
      ...item,
      firstTimestamp: item.firstTimestamp.toLocaleString(),
      lastTimestamp: item.lastTimestamp.toLocaleString(),
      // Convert Sets to counts
      uniqueDescriptions: item.uniqueDescriptions.size,
      uniqueHosts: item.uniqueHosts.size
    }));
  };

  const renderAggregatedTable = (data, groupBy, nextLevelKey, renderNestedFunc) => {
    // Determine column headers based on the current view
    const isDescriptionView = groupBy === 'description';

    return (
      <table className="table">
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                onChange={(e) => handleSelectAll(e.target.checked, data)}
                checked={
                  selectedAlerts.length > 0 &&
                  data.length > 0 &&
                  data.every(alert => selectedAlerts.some(a => a.id === alert._id && a.index === alert._index))
                }
              />
              &nbsp;&nbsp;{groupBy}
            </th>
            <th>
              {isDescriptionView
                ? '#Hosts'
                : '#Distinct Alerts'}
            </th>
            <th>#Alerts</th>
            <th>First Timestamp</th>
            <th>Last Timestamp</th>
          </tr>
        </thead>
        <tbody>
          {getAggregatedData(groupBy, data).map((item, index) => (
            <React.Fragment key={`${groupBy}-${item.key}-${index}`}>
              <tr style={{ cursor: 'pointer' }}>
                <td onClick={() => toggleRowExpansion(item.key)}>
                  <input
                    type="checkbox"
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => handleSelectGroup(e.target.checked, item.alerts)}
                    checked={areAllGroupAlertsSelected(item.alerts)}
                  />
                  &nbsp;&nbsp;{item.key}
                </td>
                <td onClick={() => toggleRowExpansion(item.key)}>{isDescriptionView ? item.uniqueHosts : item.uniqueDescriptions}</td>
                <td onClick={() => toggleRowExpansion(item.key)}>{item.count}</td>
                <td onClick={() => toggleRowExpansion(item.key)}>{item.firstTimestamp}</td>
                <td onClick={() => toggleRowExpansion(item.key)}>{item.lastTimestamp}</td>
              </tr>
              {expandedRows[item.key] && (
                <>
                  <tr>
                    <td style={{ paddingLeft: '2rem', fontWeight: 'bold' }}>{nextLevelKey}</td>
                    <th>Count</th>
                    <th>First Timestamp</th>
                    <th>Last Timestamp</th>
                  </tr>
                  {renderNestedFunc(item.alerts, item.key)}
                </>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    );
  };

  const renderSimpleTable = (data) => (
    <table className="table">
      <thead>
        <tr>
          <th>
            <input
              type="checkbox"
              onChange={(e) => handleSelectAll(e.target.checked, data)}
              checked={
                selectedAlerts.length > 0 &&
                data.length > 0 &&
                selectedAlerts.length === data.length
              }
            />
          </th>
          <th>Timestamp</th>
          <th>State</th>
          <th>Organization Host/IP</th>
          <th>Peer Host/IP</th>
          <th>Alert Description</th>
        </tr>
      </thead>
      <tbody>
        {data.map((alert) => (
          <tr key={alert._id} onClick={() => openAlertDetails(alert)} style={{ cursor: 'pointer' }}>
            <td>
              <input
                type="checkbox"
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => handleSelectRow(alert._id, alert._index, e.target.checked)}
                checked={selectedAlerts.some(a => a.id === alert._id && a.index === alert._index)}
              />
            </td>
            <td>{new Date(alert.timestamp).toLocaleString()}</td>
            <td>
              <span className={`badge bg-${getStateColor(alert.state)}`}>{alert.state || 'New'}</span>
            </td>
            <td>{alert.organization_hostname || alert.organization_ip || "No organization host/ip"}</td>
            <td>{alert.peer_hostname || alert.peer_ip || "No peer host/ip"}</td>
            <td>{alert.description}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderNestedOrganizationHosts = (data, parentKey) => {
    const groupedData = getAggregatedData('organization_hostname', data);
    return groupedData.map((item, index) => (
      <React.Fragment key={`${parentKey}-${item.key}-${index}`}>
        <tr style={{ cursor: 'pointer', backgroundColor: '#f9f9f9' }}>
          <td onClick={() => toggleRowExpansion(`${parentKey}-${item.key}`)} style={{ paddingLeft: '2rem' }}>
            <input
              type="checkbox"
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => handleSelectGroup(e.target.checked, item.alerts)}
              checked={areAllGroupAlertsSelected(item.alerts)}
            />
            &nbsp;&nbsp;{item.key}
          </td>
          <td onClick={() => toggleRowExpansion(`${parentKey}-${item.key}`)}>{item.count}</td>
          <td onClick={() => toggleRowExpansion(`${parentKey}-${item.key}`)}>{item.firstTimestamp}</td>
          <td onClick={() => toggleRowExpansion(`${parentKey}-${item.key}`)}>{item.lastTimestamp}</td>
        </tr>
        {expandedRows[`${parentKey}-${item.key}`] && (
          <>
            <tr>
              <td style={{ paddingLeft: '4rem', fontWeight: 'bold' }}>
                <input
                  type="checkbox"
                  onChange={(e) => handleSelectGroup(e.target.checked, item.alerts)}
                  checked={areAllGroupAlertsSelected(item.alerts)}
                />&nbsp;&nbsp;&nbsp;Timestamp
              </td>
              <th>Peer Host/IP</th>
              <th>State</th>
              <td colSpan="2"></td>
            </tr>
            {renderNestedAlerts(item.alerts, `${parentKey}-${item.key}`)}
          </>
        )}
      </React.Fragment>
    ));
  };

  const renderNestedAlertDescriptions = (data, parentKey) => {
    const groupedData = getAggregatedData('description', data);
    return groupedData.map((item, index) => (
      <React.Fragment key={`${parentKey}-${item.key}-${index}`}>
        <tr style={{ cursor: 'pointer', backgroundColor: '#f9f9f9' }}>
          <td onClick={() => toggleRowExpansion(`${parentKey}-${item.key}`)} style={{ paddingLeft: '2rem' }}>
            <input
              type="checkbox"
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => handleSelectGroup(e.target.checked, item.alerts)}
              checked={areAllGroupAlertsSelected(item.alerts)}
            />
            &nbsp;&nbsp;{item.key}
          </td>
          <td onClick={() => toggleRowExpansion(`${parentKey}-${item.key}`)}>{item.count}</td>
          <td onClick={() => toggleRowExpansion(`${parentKey}-${item.key}`)}>{item.firstTimestamp}</td>
          <td onClick={() => toggleRowExpansion(`${parentKey}-${item.key}`)}>{item.lastTimestamp}</td>
        </tr>
        {expandedRows[`${parentKey}-${item.key}`] && (
          <>
            <tr>
              <td style={{ paddingLeft: '4rem', fontWeight: 'bold' }}>
                <input
                  type="checkbox"
                  onChange={(e) => handleSelectGroup(e.target.checked, item.alerts)}
                  checked={areAllGroupAlertsSelected(item.alerts)}
                />&nbsp;&nbsp;&nbsp;Timestamp
              </td>
              <th>Peer Host/IP</th>
              <th>State</th>
              <td colSpan="2"></td>
            </tr>
            {renderNestedAlerts(item.alerts, `${parentKey}-${item.key}`)}
          </>
        )}
      </React.Fragment>
    ));
  };

  const renderNestedAlerts = (data, parentKey) => (
    data.map((alert, index) => (
      <tr
        key={`${parentKey}-${alert._id}-${index}`}
        onClick={() => openAlertDetails(alert)}
        style={{
          cursor: 'pointer',
          backgroundColor: '#fdfdfd',
        }}
      >
        <td style={{ paddingLeft: '4rem' }}>
          <input
            type="checkbox"
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => handleSelectRow(alert._id, alert._index, e.target.checked)}
            checked={selectedAlerts.some(a => a.id === alert._id && a.index === alert._index)}
          />&nbsp;&nbsp;&nbsp;
          {new Date(alert.timestamp).toLocaleString()}</td>
        <td>{alert.peer_hostname || alert.peer_ip}</td>
        <td>
          <span className={`badge bg-${getStateColor(alert.state)}`}>{alert.state || 'New'}</span>
        </td>
        <td colSpan="2"></td>
      </tr>
    ))
  );

  const getStateColor = (state) => {
    switch (state) {
      case 'INV':
        return 'warning text-dark';
      case 'CLSD':
        return 'success';
      case 'FA':
        return 'danger';
      default:
        return 'primary';
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="text-center p-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      );
    }

    if (activeTab === 'Alerts Aggregated') {
      return renderAggregatedTable(displayedAlerts, 'description', 'Organization Host', renderNestedOrganizationHosts);
    } else if (activeTab === 'Hosts Aggregated') {
      return renderAggregatedTable(displayedAlerts, 'organization_hostname', 'Alert Description', renderNestedAlertDescriptions);
    } else {
      return renderSimpleTable(displayedAlerts);
    }
  };

  return (
    <div className="alerts-table-container">
      {/* Tabs */}
      <div className="tabs-container">
        <ul className="nav nav-tabs">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'Simple' ? 'active' : ''}`}
              onClick={() => setActiveTab('Simple')}
            >
              All Alerts
              <span className="badge bg-danger ms-2">{tabLineCounts['Simple']}</span>
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'Alerts Aggregated' ? 'active' : ''}`}
              onClick={() => setActiveTab('Alerts Aggregated')}
            >
              Alerts Aggregated
              <span className="badge bg-danger ms-2">{tabLineCounts['Alerts Aggregated']}</span>
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'Hosts Aggregated' ? 'active' : ''}`}
              onClick={() => setActiveTab('Hosts Aggregated')}
            >
              Hosts Aggregated
              <span className="badge bg-danger ms-2">{tabLineCounts['Hosts Aggregated']}</span>
            </button>
          </li>
        </ul>
      </div>

      {/* Scrollable Table Content */}
      <div>
        {renderContent()}
      </div>
      {loading && (
        <div className="progress mb-3">
          <div
            className="progress-bar progress-bar-striped progress-bar-animated"
            role="progressbar"
            style={{ width: '100%' }}
          ></div>
        </div>
      )}
    </div>
  );
};

export default AlertsTable;
